import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from 'core/layout/MainLayout';
import { CVHttpService } from '../services';
import { HttpService } from 'core/services/http';
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    Typography,
    IconButton,
    Alert,
    Container,
    Divider,
    Chip,
    Grid
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';
import { Utils } from 'core/utils';
import WidgetCVTitle from '../components/WidgetCvTitle';
import SkillsSection from 'features/feature_user/components/profile/SkillsSection';
import EducationSection from 'features/feature_user/components/profile/EducationSection';
import LanguageSection from 'features/feature_user/components/profile/LanguageSection';
import ReferenceSection from 'features/feature_user/components/profile/ReferenceSection';
import CertificationSection from 'features/feature_user/components/profile/CertificationSection';
import JobExperienceSection from 'features/feature_user/components/profile/JobExperienceSection';
import MarkdownRenderer from '../components/MarkdownRenderer';

const formatText = (value, fallback = '—') => {
    if (!Utils.normalizeText) return value ?? fallback;
    return Utils.normalizeText(value, fallback);
};

const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    } catch {
        return dateString;
    }
};

function ShareableCVPage() {
    const { shareUUID } = useParams();
    const navigate = useNavigate();
    const [cv, setCv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [sections, setSections] = useState({
        experiences: [],
        education: [],
        skills: [],
        languages: [],
        certificates: [],
        references: [],
        awards: []
    });
    const [loadingSections, setLoadingSections] = useState(true);

    useEffect(() => {
        if (cv?.id) {
            loadSections();
        }
    }, [cv]);

    const loadCV = async () => {
        if (!shareUUID) {
            setError('Invalid CV link');
            setLoading(false);
            return;
        }

        try {
            const data = await CVHttpService.loadCVByShareUUID(shareUUID);
            setCv(data);
        } catch (err) {
            console.error('Error loading CV:', err);
            setError('CV not found or is not available');
        } finally {
            setLoading(false);
        }
    };

    const loadSections = async () => {
        if (!cv?.id || !shareUUID) return;
        setLoadingSections(true);
        try {
            const service = new HttpService();
            // Send share_uuid parameter for security verification
            const [experiences, education, skills, languages, certificates, references, awards] = await Promise.all([
                service.get(`/job/cv/jobexperience/?cv=${cv.id}&share_uuid=${shareUUID}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/education/?cv=${cv.id}&share_uuid=${shareUUID}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/userskill/?cv=${cv.id}&share_uuid=${shareUUID}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/userlanguage/?cv=${cv.id}&share_uuid=${shareUUID}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/certificate/?cv=${cv.id}&share_uuid=${shareUUID}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/reference/?cv=${cv.id}&share_uuid=${shareUUID}`, null).catch(() => ({ results: [] })),
                service.get(`/job/cv/award/?cv=${cv.id}&share_uuid=${shareUUID}`, null).catch(() => ({ results: [] }))
            ]);

            setSections({
                experiences: Array.isArray(experiences) ? experiences : (experiences?.results || []),
                education: Array.isArray(education) ? education : (education?.results || []),
                skills: Array.isArray(skills) ? skills : (skills?.results || []),
                languages: Array.isArray(languages) ? languages : (languages?.results || []),
                certificates: Array.isArray(certificates) ? certificates : (certificates?.results || []),
                references: Array.isArray(references) ? references : (references?.results || []),
                awards: Array.isArray(awards) ? awards : (awards?.results || [])
            });
        } catch (err) {
            console.error('Error loading sections:', err);
        } finally {
            setLoadingSections(false);
        }
    };

    useEffect(() => {
        loadCV();
    }, [shareUUID]);

    const handleDownloadPDF = async () => {
        if (!shareUUID) return;
        setDownloading(true);
        try {
            await CVHttpService.downloadPDFByShareUUID(shareUUID);
        } catch (err) {
            alert('Failed to download PDF');
        } finally {
            setDownloading(false);
        }
    };

    const handleCopyURL = async () => {
        const url = window.location.href;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            alert('Failed to copy URL');
        }
    };

    const handlePrint = () => {
        if (cv?.share_uuid) {
            window.open(`/cv/print/${cv.share_uuid}`, '_blank');
        } else if (cv?.id) {
            window.open(`/cv/print/id/${cv.id}`, '_blank');
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <Container maxWidth="lg" sx={{ mt: 6 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                        <CircularProgress />
                    </Box>
                </Container>
            </MainLayout>
        );
    }

    if (error || !cv) {
        return (
            <MainLayout>
                <Container maxWidth="lg" sx={{ mt: 6 }}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error || 'CV not found or is not available'}
                    </Alert>
                </Container>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
                <Stack spacing={3}>
                    {/* Header Actions */}
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
                            <Box>
                                <Typography variant="h4" fontWeight={700} gutterBottom>
                                    {formatText(cv.title)}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {formatText(`${cv.name || ''} ${cv.last_name || ''}`.trim() || 'No name')}
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <IconButton
                                    onClick={handlePrint}
                                    color="primary"
                                    title="Print View"
                                >
                                    <PrintIcon />
                                </IconButton>
                                <IconButton
                                    onClick={handleCopyURL}
                                    color={copied ? 'success' : 'default'}
                                    title="Copy this page URL"
                                >
                                    {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                </IconButton>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadPDF}
                                    disabled={downloading}
                                    size="small"
                                >
                                    {downloading ? 'Downloading...' : 'Download PDF'}
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>

                    {/* CV Content */}
                    {(() => {
                        // Check if this is an AI-generated CV (markdown format)
                        const isAIGenerated = cv.description && (
                            cv.description.trim().startsWith('#') || 
                            cv.description.includes('##') ||
                            (cv.description.includes('---') && cv.description.split('\n').length > 10)
                        );
                        
                        if (isAIGenerated) {
                            // For AI-generated CVs, show the full markdown content
                            return (
                                <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                    <MarkdownRenderer markdown={cv.description} />
                                </Paper>
                            );
                        }
                        
                        // For regular CVs, show the structured format
                        return (
                            <>
                                <WidgetCVTitle cv={cv} editable={false} />

                                {/* About Section - Show at top */}
                                {cv.description && (
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="h6" fontWeight={600} gutterBottom>
                                            About
                                        </Typography>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {formatText(cv.description)}
                                        </Typography>
                                    </Paper>
                                )}

                                {/* CV Sections */}
                                {loadingSections ? (
                                    <Box display="flex" justifyContent="center" p={3}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : (
                                    <>
                                        {sections.experiences.length > 0 && (
                                            <JobExperienceSection cv={cv} readonly />
                                        )}
                                        {sections.skills.length > 0 && (
                                            <SkillsSection cv={cv} readonly />
                                        )}
                                        {sections.education.length > 0 && (
                                            <EducationSection cv={cv} readonly />
                                        )}
                                        {sections.languages.length > 0 && (
                                            <LanguageSection cv={cv} readonly />
                                        )}
                                        {sections.references.length > 0 && (
                                            <ReferenceSection cv={cv} readonly />
                                        )}
                                        {sections.certificates.length > 0 && (
                                            <CertificationSection cv={cv} readonly />
                                        )}
                                        {sections.awards.length > 0 && (
                                            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                                    Awards & Achievements
                                                </Typography>
                                                <Divider sx={{ mb: 2 }} />
                                                <Stack spacing={2}>
                                                    {sections.awards.map((award) => (
                                                        <Box key={award.id}>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                {formatText(award.title)}
                                                            </Typography>
                                                            {award.awarded_date && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatDate(award.awarded_date)}
                                                                </Typography>
                                                            )}
                                                            {award.description && (
                                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                    {formatText(award.description)}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Paper>
                                        )}
                                    </>
                                )}
                            </>
                        );
                    })()}

                    {/* Interests Section - Show at bottom */}
                    {cv.interests && (
                        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>
                                Interests
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                {formatText(cv.interests)}
                            </Typography>
                        </Paper>
                    )}

                    <Alert severity="info" sx={{ mt: 2 }}>
                        This is a public view of the CV. All information is visible to anyone with this link.
                    </Alert>
                </Stack>
            </Container>
        </MainLayout>
    );
}

export default ShareableCVPage;
